import { ExternalLink, X } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { formatDate } from "../../lib/format";
import {
  getARRecoveryNotesByAccount,
  getActivityByRecord,
  getAuditByRecord,
  getCollectionLetterHistoryByAccount,
  getOperationalRecordByTarget,
  getTicklersByRecord,
  type UniversalOperationalFoundationState,
  type UniversalTickler,
} from "../../lib/universalOperationalEngines";

type RecordDetailTab = "timeline" | "notes" | "ticklers" | "letters" | "audit";

type UniversalOperationalRecordDrawerProps = {
  open: boolean;
  tickler: UniversalTickler | null;
  state: UniversalOperationalFoundationState;
  persistenceStatus: string;
  relatedRoute?: string;
  onClose: () => void;
  onStatusChange: (tickler: UniversalTickler, status: UniversalTickler["status"]) => void;
  onAddNote: (tickler: UniversalTickler, note: string) => void;
  onAddActivity: (tickler: UniversalTickler, note: string) => void;
};

const baseTabs: Array<{ key: RecordDetailTab; label: string }> = [
  { key: "timeline", label: "Timeline" },
  { key: "notes", label: "Notes" },
  { key: "ticklers", label: "Ticklers" },
  { key: "audit", label: "Audit" },
];

export function UniversalOperationalRecordDrawer({
  open,
  tickler,
  state,
  persistenceStatus,
  relatedRoute = "",
  onClose,
  onStatusChange,
  onAddNote,
  onAddActivity,
}: UniversalOperationalRecordDrawerProps) {
  const [selectedTab, setSelectedTab] = useState<RecordDetailTab>("timeline");
  const [entryText, setEntryText] = useState("");
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    closeButtonRef.current?.focus();

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, open]);

  useEffect(() => {
    if (open) {
      setSelectedTab("timeline");
      setEntryText("");
    }
  }, [open, tickler?.id]);

  const recordActivity = useMemo(() => {
    if (!tickler) return [];
    return getActivityByRecord(state, tickler.workspace, tickler.recordType, tickler.recordId);
  }, [state, tickler]);

  const universalNotes = useMemo(() => {
    if (!tickler) return [];
    return state.businessNotes.filter((note) => note.workspace === tickler.workspace && note.recordId === tickler.recordId);
  }, [state.businessNotes, tickler]);

  const arNotes = useMemo(() => {
    if (!tickler || tickler.workspace !== "ar-recovery-os") return [];
    return getARRecoveryNotesByAccount(state, tickler.recordId);
  }, [state, tickler]);

  const relatedTicklers = useMemo(() => {
    if (!tickler) return [];
    return getTicklersByRecord(state, tickler.workspace, tickler.recordType, tickler.recordId);
  }, [state, tickler]);

  const letterHistory = useMemo(() => {
    if (!tickler || tickler.workspace !== "ar-recovery-os") return [];
    return getCollectionLetterHistoryByAccount(state, tickler.recordId);
  }, [state, tickler]);

  const auditEntries = useMemo(() => {
    if (!tickler) return [];
    return getAuditByRecord(state, tickler.workspace, tickler.recordType, tickler.recordId);
  }, [state, tickler]);
  const registryRecord = useMemo(() => {
    if (!tickler) return null;
    return getOperationalRecordByTarget(state, tickler.workspace, tickler.recordType, tickler.recordId);
  }, [state, tickler]);
  const visibleTabs = useMemo(() => {
    if (tickler?.workspace === "ar-recovery-os") {
      return [...baseTabs.slice(0, 3), { key: "letters" as const, label: "Letters" }, ...baseTabs.slice(3)];
    }
    return baseTabs;
  }, [tickler?.workspace]);

  if (!open || !tickler) return null;

  function handleEntrySubmit(kind: "note" | "activity") {
    if (!tickler || !entryText.trim()) return;
    if (kind === "note") {
      onAddNote(tickler, entryText.trim());
    } else {
      onAddActivity(tickler, entryText.trim());
    }
    setEntryText("");
    setSelectedTab(kind === "note" ? "notes" : "timeline");
  }

  return (
    <div className="fixed inset-0 z-[70] bg-slate-950/70 backdrop-blur-sm" role="presentation">
      <button type="button" className="absolute inset-0 h-full w-full cursor-default" aria-label="Close operational record details" onClick={onClose} />
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="operational-record-drawer-title"
        className="relative ml-auto flex h-full w-full max-w-4xl flex-col overflow-hidden border-l border-white/10 bg-[#07111F] text-white shadow-2xl shadow-black/40"
      >
        <header className="shrink-0 border-b border-white/10 px-4 py-4 sm:px-6">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-cyan-100">Operational Record Detail</p>
              <h2 id="operational-record-drawer-title" className="mt-2 font-display text-3xl font-bold leading-tight text-white sm:text-4xl">
                {tickler.reminderType}
              </h2>
              <p className="mt-2 max-w-3xl text-sm font-semibold leading-7 text-slate-300">{tickler.notes}</p>
              {registryRecord ? (
                <p className="mt-2 text-sm font-black uppercase tracking-[0.12em] text-cyan-100">
                  {registryRecord.recordName} / {registryRecord.status || "active"}
                </p>
              ) : null}
            </div>
            <button
              ref={closeButtonRef}
              type="button"
              onClick={onClose}
              className="focus-ring grid size-11 shrink-0 place-items-center rounded-xl border border-white/10 bg-white/[.06] text-slate-200"
              aria-label="Close record detail drawer"
            >
              <X className="size-5" aria-hidden="true" />
            </button>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <RecordBadge label={formatRecordLabel(tickler.workspace)} />
            <RecordBadge label={formatRecordLabel(tickler.recordType)} />
            <RecordBadge label={tickler.status} />
            <RecordBadge label={tickler.priority} />
            <RecordBadge label={`Assigned: ${tickler.assignedUser}`} />
          </div>
        </header>

        <div className="luxury-scrollbar flex-1 overflow-y-auto px-4 py-5 sm:px-6">
          <section className="grid gap-4 lg:grid-cols-2">
            <RecordInfo label="Related record ID" value={tickler.recordId} />
            {registryRecord ? <RecordInfo label="Registry record" value={registryRecord.recordName} /> : null}
            <RecordInfo label="Due" value={`${formatDate(tickler.dueDate)} at ${tickler.dueTime}`} />
            <RecordInfo label="Related account, client, or project" value={`${formatRecordLabel(tickler.recordType)} / ${tickler.recordId}`} />
            {registryRecord?.owner ? <RecordInfo label="Record owner" value={registryRecord.owner} /> : null}
            <RecordInfo label="Next action" value={tickler.status === "Completed" ? "Review completed activity." : tickler.notes} />
            <RecordInfo label="Sync source" value={persistenceStatus} />
            <RecordInfo label="Last updated" value={formatDate(tickler.updatedAt)} />
          </section>

          <section className="mt-5 rounded-3xl border border-cyan-300/15 bg-cyan-300/[.06] p-4">
            <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-cyan-100">Actions</p>
                <p className="mt-2 text-sm font-semibold leading-7 text-slate-300">
                  Actions update the shared operational state and write timeline or audit history where appropriate.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {(["Completed", "Snoozed", "Escalated"] as const).map((status) => (
                  <button
                    key={status}
                    type="button"
                    aria-label={`${status === "Completed" ? "Complete" : status} operational record`}
                    onClick={() => onStatusChange(tickler, status)}
                    className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-10 items-center rounded-full px-3 text-xs font-extrabold"
                  >
                    {status === "Completed" ? "Complete" : status}
                  </button>
                ))}
                {relatedRoute ? (
                  <Link to={relatedRoute} className="business-center-os-button business-center-os-button--primary focus-ring inline-flex min-h-10 items-center gap-2 rounded-full px-3 text-xs font-extrabold">
                    Open Related Record
                    <ExternalLink className="size-3" aria-hidden="true" />
                  </Link>
                ) : (
                  <button type="button" disabled className="inline-flex min-h-10 items-center rounded-full border border-white/10 bg-white/[.04] px-3 text-xs font-extrabold text-slate-300 opacity-60">
                    Related route unavailable
                  </button>
                )}
              </div>
            </div>
            <label className="mt-4 grid gap-2 text-sm font-extrabold text-white">
              Note or activity detail
              <textarea
                value={entryText}
                onChange={(event) => setEntryText(event.target.value)}
                rows={4}
                className="business-center-os-field business-center-os-field--dark focus-ring rounded-2xl px-4 py-3 text-sm font-semibold"
              />
            </label>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                disabled={!entryText.trim()}
                aria-label="Add note to operational record"
                onClick={() => handleEntrySubmit("note")}
                className="business-center-os-button business-center-os-button--primary focus-ring inline-flex min-h-11 items-center rounded-full px-4 text-sm font-extrabold disabled:opacity-60"
              >
                Add Note
              </button>
              <button
                type="button"
                disabled={!entryText.trim()}
                aria-label="Add activity to operational record"
                onClick={() => handleEntrySubmit("activity")}
                className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-11 items-center rounded-full px-4 text-sm font-extrabold disabled:opacity-60"
              >
                Add Activity
              </button>
            </div>
          </section>

          <section className="mt-5">
            <div className="flex flex-wrap gap-2" role="tablist" aria-label="Operational record detail tabs">
              {visibleTabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={selectedTab === tab.key}
                  aria-controls={`operational-record-tab-${tab.key}`}
                  id={`operational-record-tab-button-${tab.key}`}
                  onClick={() => setSelectedTab(tab.key)}
                  className={`focus-ring inline-flex min-h-10 items-center rounded-full border px-4 text-sm font-extrabold ${
                    selectedTab === tab.key
                      ? "border-cyan-300/30 bg-cyan-300/15 text-cyan-50"
                      : "border-white/10 bg-white/[.04] text-slate-300"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div
              id={`operational-record-tab-${selectedTab}`}
              role="tabpanel"
              aria-labelledby={`operational-record-tab-button-${selectedTab}`}
              className="mt-4 rounded-3xl border border-white/10 bg-white/[.04] p-4"
            >
              {selectedTab === "timeline" ? (
                <RecordList
                  emptyText="No timeline activity has been recorded for this operational record yet."
                  rows={recordActivity.map((event) => ({
                    id: event.id,
                    title: event.action,
                    detail: event.description,
                    meta: `${formatDate(event.timestamp)} / ${event.user} / ${event.outcome}`,
                  }))}
                />
              ) : null}

              {selectedTab === "notes" ? (
                <div className="grid gap-4">
                  <RecordList
                    title="Universal business notes"
                    emptyText="No universal business notes are linked to this record."
                    rows={universalNotes.map((note) => ({
                      id: note.id,
                      title: note.noteCategory,
                      detail: note.content,
                      meta: `${formatDate(note.timestamp)} / ${note.author} / ${note.visibility}`,
                    }))}
                  />
                  {tickler.workspace === "ar-recovery-os" ? (
                    <RecordList
                      title="AR Recovery notes"
                      emptyText="No AR Recovery notes are linked to this account."
                      rows={arNotes.map((note) => ({
                        id: note.id,
                        title: note.noteType,
                        detail: note.content,
                        meta: `${formatDate(note.timestamp)} / ${note.collector} / ${note.outcome}`,
                      }))}
                    />
                  ) : null}
                </div>
              ) : null}

              {selectedTab === "ticklers" ? (
                <RecordList
                  emptyText="No related ticklers are linked to this record."
                  rows={relatedTicklers.map((item) => ({
                    id: item.id,
                    title: item.reminderType,
                    detail: item.notes,
                    meta: `${formatDate(item.dueDate)} at ${item.dueTime} / ${item.status} / ${item.assignedUser}`,
                    actions: (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {(["Completed", "Snoozed", "Escalated"] as const).map((status) => (
                          <button
                            key={status}
                            type="button"
                            aria-label={`${status === "Completed" ? "Complete" : status} related tickler`}
                            onClick={() => onStatusChange(item, status)}
                            className="focus-ring inline-flex min-h-9 items-center rounded-full border border-white/10 bg-white/[.06] px-3 text-xs font-extrabold text-slate-100"
                          >
                            {status === "Completed" ? "Complete" : status}
                          </button>
                        ))}
                      </div>
                    ),
                  }))}
                />
              ) : null}

              {selectedTab === "letters" ? (
                tickler.workspace === "ar-recovery-os" ? (
                  <RecordList
                    emptyText="No collection letter history is linked to this AR account."
                    rows={letterHistory.map((letter) => ({
                      id: letter.id,
                      title: letter.title,
                      detail: letter.detail,
                      meta: `${formatDate(letter.createdAt)} / ${letter.templateName} / ${letter.deliveryStatus}`,
                    }))}
                  />
                ) : (
                  <EmptyRecordText text="Letter history is only available for CollabRecover AR Solutions records." />
                )
              ) : null}

              {selectedTab === "audit" ? (
                <RecordList
                  emptyText="No audit history is linked to this record."
                  rows={auditEntries.map((entry) => ({
                    id: entry.id,
                    title: entry.fieldChanged,
                    detail: `${entry.previousValue} to ${entry.newValue}`,
                    meta: `${formatDate(entry.timestamp)} / ${entry.user} / ${entry.visibility}`,
                  }))}
                />
              ) : null}
            </div>
          </section>
        </div>
      </section>
    </div>
  );
}

function RecordBadge({ label }: { label: string }) {
  return <span className="rounded-full border border-cyan-300/20 bg-cyan-300/[.08] px-3 py-1 text-xs font-black uppercase tracking-[0.12em] text-cyan-100">{label}</span>;
}

function RecordInfo({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[.04] p-4">
      <p className="text-xs font-black uppercase tracking-[0.14em] text-cyan-100">{label}</p>
      <p className="mt-2 break-words text-sm font-semibold leading-6 text-slate-200">{value || "Not available"}</p>
    </div>
  );
}

function RecordList({
  title,
  emptyText,
  rows,
}: {
  title?: string;
  emptyText: string;
  rows: Array<{ id: string; title: string; detail: string; meta: string; actions?: ReactNode }>;
}) {
  return (
    <div className="grid gap-3">
      {title ? <p className="text-xs font-black uppercase tracking-[0.16em] text-cyan-100">{title}</p> : null}
      {rows.length ? (
        rows.map((row) => (
          <article key={row.id} className="rounded-2xl border border-white/10 bg-[#071A2F] p-4">
            <p className="text-sm font-black text-white">{row.title}</p>
            <p className="mt-2 text-sm font-semibold leading-7 text-slate-300">{row.detail}</p>
            <p className="mt-2 text-xs font-bold uppercase tracking-[0.12em] text-slate-400">{row.meta}</p>
            {row.actions}
          </article>
        ))
      ) : (
        <EmptyRecordText text={emptyText} />
      )}
    </div>
  );
}

function EmptyRecordText({ text }: { text: string }) {
  return (
    <div className="rounded-2xl border border-dashed border-white/15 bg-white/[.03] p-4">
      <p className="text-sm font-semibold leading-7 text-slate-300">{text}</p>
    </div>
  );
}

function formatRecordLabel(value: string) {
  return value
    .replace(/[-_]/g, " ")
    .replace(/\bos\b/gi, "OS")
    .replace(/\bar\b/gi, "AR")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}
